/**
 * /approvals/policies — approval policy management (admin only).
 *
 * Full CRUD for approval policies: list, create, activate/deactivate.
 * The main /approvals page only reads policies to find the active policy ID
 * for recommendations — it has NO policy management UI, so this page is not
 * a duplicate.
 *
 * Endpoints (prefix /api/money/approvals):
 *   GET    /policies              — list all policies for entity
 *   POST   /policies              — create (admin)
 *   POST   /policies/{id}/activate — activate (admin)
 *
 * Deactivation: no dedicated endpoint — activate a different policy; only one
 * is active at a time per entity.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
} from "@mui/material";
import PolicyIcon from "@mui/icons-material/Policy";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import {
  approvalPoliciesQueryOptions,
  entityUsersQueryOptions,
  myEntitiesQueryOptions,
} from "@/lib/queries";
import { api, errorMessage } from "@/lib/api";
import { MoneyInput } from "@/components/MoneyInput";
import type { ApprovalPolicy } from "@/lib/types";

export const Route = createFileRoute("/_auth/approvals/policies")({
  component: ApprovalPoliciesPage,
});

// ──────────────────────────────────────────────────────────────
// Types for create form
// ──────────────────────────────────────────────────────────────
interface PolicyTierDraft {
  max_amount: string; // empty string = no upper bound (top tier)
  approver_roles: string; // comma-separated input
  approver_ids: string[];
  any_or_all: "any" | "all";
  required_approvals: number;
  allow_requester_approval: boolean;
  require_distinct_approver: boolean;
  ai_can_skip: boolean;
}

interface CreatePolicyBody {
  name: string;
  scope: { source_type: string | string[]; min_amount?: number };
  tiers: Array<{
    max_amount: number | null;
    approver_roles: string[];
    approver_ids: string[];
    any_or_all: "any" | "all";
    required_approvals: number;
    allow_requester_approval: boolean;
    require_distinct_approver: boolean;
    ai_can_skip: boolean;
  }>;
  ai_assist: boolean;
}

interface ControlMaturityAssessment {
  current_stage: { code: string; label: string; meaning: string };
  recommended_stage: { code: string; label: string; meaning: string };
  evidence: {
    active_users: number;
    journals_90d: number;
    manual_journals_90d: number;
    approval_requests_90d: number;
    active_workflow_policies: number;
  };
  recommendations: Array<{ priority: string; action: string; why: string }>;
  automatic_changes: boolean;
  next_periodic_review_at: string;
  progression: {
    direction: string;
    summary: string;
  };
  implementation: {
    readiness: "simple_mode" | "needs_reviewer" | "ready_to_propose" | "proposal_pending";
    next_action: string;
    profile: {
      operating_model: string;
      declared_accounting_people_count: number;
      confirmed: boolean;
    } | null;
    available_reviewers: Array<{ id: string; name: string; email: string }>;
    pending_proposal: ControlPolicyProposal | null;
    open_review_notice: { id: string; created_at: string } | null;
  };
  help_path: string;
}

interface ControlPolicyProposal {
  id: string;
  status: string;
  policy_name: string;
  source_type: string;
  minimum_amount: string;
  reviewer_user_id: string;
  rationale: string;
  activation_confirmation: string;
  expires_at: string;
}

const SOURCE_TYPE_OPTIONS = [
  { value: "purchase_order", label: "Purchase Order" },
  { value: "ap_invoice", label: "AP Invoice" },
  { value: "ar_invoice", label: "AR Invoice" },
  { value: "manual_journal", label: "Manual Journal" },
  { value: "sub_pay_app", label: "Subcontractor Pay Application" },
];

const ROLE_OPTIONS = ["pm", "manager", "accountant", "controller", "cfo", "ap_clerk", "ar_clerk", "admin"];

// ──────────────────────────────────────────────────────────────
// Helper
// ──────────────────────────────────────────────────────────────
function fmtMoney(val: number | null): string {
  if (val == null) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(val);
}

function fmtDate(s: string): string {
  return new Date(s).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

// ──────────────────────────────────────────────────────────────
// Create policy dialog
// ──────────────────────────────────────────────────────────────
function CreatePolicyDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [sourceType, setSourceType] = useState("purchase_order");
  const [minimumAmount, setMinimumAmount] = useState("");
  const [aiAssist, setAiAssist] = useState(true);
  const myEntitiesQ = useQuery(myEntitiesQueryOptions());
  const activeEntityId = myEntitiesQ.data?.find((entity) => entity.is_current)?.id ?? "";
  const entityUsersQ = useQuery(entityUsersQueryOptions(activeEntityId));
  const entityUsers = entityUsersQ.data ?? [];
  const [tiers, setTiers] = useState<PolicyTierDraft[]>([
    {
      max_amount: "",
      approver_roles: "manager",
      approver_ids: [],
      any_or_all: "any",
      required_approvals: 1,
      allow_requester_approval: false,
      require_distinct_approver: true,
      ai_can_skip: false,
    },
  ]);

  const createMutation = useMutation({
    mutationFn: (body: CreatePolicyBody) =>
      api<ApprovalPolicy>("/api/money/approvals/policies", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["approvals", "policies"] });
      handleClose();
    },
  });

  function handleClose() {
    setName("");
    setSourceType("purchase_order");
    setMinimumAmount("");
    setAiAssist(true);
    setTiers([
      {
        max_amount: "",
        approver_roles: "manager",
        approver_ids: [],
        any_or_all: "any",
        required_approvals: 1,
        allow_requester_approval: false,
        require_distinct_approver: true,
        ai_can_skip: false,
      },
    ]);
    onClose();
  }

  function addTier() {
    setTiers((prev) => [
      ...prev,
      {
        max_amount: "",
        approver_roles: "admin",
        approver_ids: [],
        any_or_all: "any",
        required_approvals: 1,
        allow_requester_approval: false,
        require_distinct_approver: true,
        ai_can_skip: false,
      },
    ]);
  }

  function removeTier(idx: number) {
    setTiers((prev) => prev.filter((_, i) => i !== idx));
  }

  function updateTier(idx: number, patch: Partial<PolicyTierDraft>) {
    setTiers((prev) =>
      prev.map((t, i) => (i === idx ? { ...t, ...patch } : t)),
    );
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const body: CreatePolicyBody = {
      name: name.trim(),
      scope: {
        source_type: sourceType,
        ...(minimumAmount ? { min_amount: parseFloat(minimumAmount) } : {}),
      },
      ai_assist: aiAssist,
      tiers: tiers.map((t) => ({
        max_amount: t.max_amount ? parseFloat(t.max_amount) : null,
        approver_roles: t.approver_roles
          .split(",")
          .map((r) => r.trim())
          .filter(Boolean),
        approver_ids: t.approver_ids,
        any_or_all: t.any_or_all,
        required_approvals: t.any_or_all === "all" ? 1 : t.required_approvals,
        allow_requester_approval: t.allow_requester_approval,
        require_distinct_approver: t.require_distinct_approver,
        ai_can_skip: t.ai_can_skip,
      })),
    };
    createMutation.mutate(body);
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle sx={{ fontSize: 15, fontWeight: 600, pb: 1 }}>
          New Approval Policy
        </DialogTitle>
        <DialogContent dividers>
          <div className="space-y-4 py-1">
            {/* Name */}
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1 dark:text-slate-400">
                Policy name
              </label>
              <input
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. PO approval — standard"
                className="w-full border border-slate-300 rounded-md px-3 py-1.5 text-sm dark:bg-gray-900 dark:border-gray-700"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1 dark:text-slate-400">
                Minimum amount requiring review
              </label>
              <MoneyInput
                value={minimumAmount}
                onChange={setMinimumAmount}
                placeholder="Leave blank to review every amount"
              />
              <p className="mt-1 text-[11px] text-slate-400">
                Activity below this inclusive floor stays direct.
              </p>
            </div>

            {/* Source type */}
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1 dark:text-slate-400">
                Applies to
              </label>
              <select
                value={sourceType}
                onChange={(e) => setSourceType(e.target.value)}
                className="w-full border border-slate-300 rounded-md px-3 py-1.5 text-sm bg-white dark:bg-gray-900 dark:border-gray-700"
              >
                {SOURCE_TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>

            {/* AI assist */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="ai-assist"
                checked={aiAssist}
                onChange={(e) => setAiAssist(e.target.checked)}
                className="rounded"
              />
              <label
                htmlFor="ai-assist"
                className="text-sm text-slate-700 dark:text-slate-300"
              >
                Enable AI routing assist
              </label>
              <Tooltip title="When enabled, the AI layer suggests the most appropriate approver for each tier based on past decisions and vendor history.">
                <InfoOutlinedIcon
                  sx={{ fontSize: 14, color: "#94a3b8", cursor: "help" }}
                />
              </Tooltip>
            </div>

            {/* Tiers */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-medium text-slate-600 dark:text-slate-400">
                  Approval tiers
                </label>
                <button
                  type="button"
                  onClick={addTier}
                  className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-0.5"
                >
                  <AddIcon sx={{ fontSize: 14 }} />
                  Add tier
                </button>
              </div>
              <div className="space-y-3">
                {tiers.map((tier, idx) => (
                  <div
                    key={idx}
                    className="border border-slate-200 rounded-lg p-3 bg-slate-50 dark:bg-gray-950 dark:border-gray-800"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">
                        Tier {idx + 1}
                        {idx === tiers.length - 1 && (
                          <span className="ml-1 text-slate-400">(top tier — no upper bound)</span>
                        )}
                      </span>
                      {tiers.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeTier(idx)}
                          className="text-xs text-red-500 hover:text-red-600"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-[10px] text-slate-500 mb-0.5 dark:text-slate-400">
                          Max amount (USD, blank = unlimited)
                        </label>
                        <MoneyInput
                          value={tier.max_amount}
                          onChange={(v) => updateTier(idx, { max_amount: v })}
                          placeholder="e.g. 10000"
                          className="text-xs dark:bg-gray-900 dark:border-gray-700"
                        />
                      </div>
                      <div className="col-span-2">
                        <div className="mb-1 flex items-center justify-between">
                          <label className="block text-[10px] text-slate-500 dark:text-slate-400">
                            Named approvers (recommended for exact SoD)
                          </label>
                          {entityUsersQ.isLoading && <CircularProgress size={10} />}
                        </div>
                        <div className="grid max-h-28 grid-cols-1 gap-1 overflow-y-auto rounded-md border border-slate-200 bg-white p-2 sm:grid-cols-2 dark:border-gray-700 dark:bg-gray-900">
                          {entityUsers.length === 0 ? (
                            <p className="col-span-2 text-[10px] text-slate-400">
                              No active entity users are available. Add users under Settings → Team.
                            </p>
                          ) : entityUsers.map((member) => (
                            <label key={member.user_id} className="flex items-start gap-1.5 text-xs text-slate-700 dark:text-slate-300">
                              <input
                                type="checkbox"
                                checked={tier.approver_ids.includes(member.user_id)}
                                onChange={(event) => {
                                  const next = event.target.checked
                                    ? [...tier.approver_ids, member.user_id]
                                    : tier.approver_ids.filter((id) => id !== member.user_id);
                                  updateTier(idx, { approver_ids: next });
                                }}
                                className="mt-0.5 rounded"
                              />
                              <span className="min-w-0">
                                <span className="block truncate font-medium">
                                  {member.user_full_name || member.user_email || member.user_id.slice(0, 8)}
                                </span>
                                <span className="block text-[10px] text-slate-400">{member.role}</span>
                              </span>
                            </label>
                          ))}
                        </div>
                        <p className="mt-1 text-[10px] text-slate-400">
                          When people are named, that exact active entity-member set is frozen; the role label documents the business authority.
                        </p>
                      </div>
                      <div>
                        <label className="block text-[10px] text-slate-500 mb-0.5 dark:text-slate-400">
                          Approver roles (comma-separated)
                        </label>
                        <input
                          required
                          value={tier.approver_roles}
                          onChange={(e) =>
                            updateTier(idx, { approver_roles: e.target.value })
                          }
                          placeholder={ROLE_OPTIONS.join(", ")}
                          className="w-full border border-slate-300 rounded-md px-2 py-1 text-xs dark:bg-gray-900 dark:border-gray-700"
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] text-slate-500 mb-0.5 dark:text-slate-400">
                          Approval requirement
                        </label>
                        <select
                          value={tier.any_or_all}
                          onChange={(e) =>
                            updateTier(idx, {
                              any_or_all: e.target.value as "any" | "all",
                            })
                          }
                          className="w-full border border-slate-300 rounded-md px-2 py-1 text-xs bg-white dark:bg-gray-900 dark:border-gray-700"
                        >
                          <option value="any">Any one approver</option>
                          <option value="all">All approvers</option>
                        </select>
                      </div>
                      {tier.any_or_all === "any" && (
                        <div>
                          <label className="block text-[10px] text-slate-500 mb-0.5 dark:text-slate-400">
                            Signatures required
                          </label>
                          <input
                            type="number"
                            min={1}
                            value={tier.required_approvals}
                            onChange={(e) =>
                              updateTier(idx, {
                                required_approvals: Math.max(1, Number(e.target.value) || 1),
                              })
                            }
                            className="w-full border border-slate-300 rounded-md px-2 py-1 text-xs dark:bg-gray-900 dark:border-gray-700"
                          />
                        </div>
                      )}
                      <div className="flex items-end pb-0.5">
                        <label className="flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-400">
                          <input
                            type="checkbox"
                            checked={tier.ai_can_skip}
                            onChange={(e) =>
                              updateTier(idx, { ai_can_skip: e.target.checked })
                            }
                            className="rounded"
                          />
                          AI may recommend escalation
                        </label>
                      </div>
                      <div className="col-span-2 rounded-md border border-emerald-100 bg-emerald-50/70 p-2 dark:border-emerald-900 dark:bg-emerald-950/30">
                        <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-400">
                          Segregation of duties
                        </p>
                        <div className="flex flex-wrap gap-x-5 gap-y-1">
                          <label className="flex items-center gap-1.5 text-xs text-slate-700 dark:text-slate-300">
                            <input
                              type="checkbox"
                              checked={!tier.allow_requester_approval}
                              onChange={(e) =>
                                updateTier(idx, { allow_requester_approval: !e.target.checked })
                              }
                              className="rounded"
                            />
                            Block requester self-approval
                          </label>
                          <label className="flex items-center gap-1.5 text-xs text-slate-700 dark:text-slate-300">
                            <input
                              type="checkbox"
                              checked={tier.require_distinct_approver}
                              onChange={(e) =>
                                updateTier(idx, { require_distinct_approver: e.target.checked })
                              }
                              className="rounded"
                            />
                            Require a different person than prior tiers
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {createMutation.isError && (
              <p className="text-xs text-red-500">
                {(createMutation.error as Error).message}
              </p>
            )}
          </div>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <button
            type="button"
            onClick={handleClose}
            className="px-3 py-1.5 text-sm rounded-md border border-slate-300 text-slate-600 hover:bg-slate-50 transition dark:border-gray-700 dark:text-slate-300 dark:hover:bg-gray-800"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={createMutation.isPending || !name.trim()}
            className="px-4 py-1.5 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 transition flex items-center gap-1.5"
          >
            {createMutation.isPending && <CircularProgress size={12} sx={{ color: "#fff" }} />}
            Create policy
          </button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

// ──────────────────────────────────────────────────────────────
// Policy row
// ──────────────────────────────────────────────────────────────
function PolicyRow({ policy }: { policy: ApprovalPolicy }) {
  const qc = useQueryClient();
  const activateMutation = useMutation({
    mutationFn: () =>
      api<ApprovalPolicy>(`/api/money/approvals/policies/${policy.id}/activate`, {
        method: "POST",
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["approvals", "policies"] }),
  });

  const sourceTypes = policy.scope_json?.source_type;
  const sourceLabel = Array.isArray(sourceTypes)
    ? sourceTypes.join(", ")
    : (sourceTypes as string) ?? "—";
  const controlled = policy.tiers_json.every(
    (tier) =>
      typeof tier.allow_requester_approval === "boolean" &&
      typeof tier.require_distinct_approver === "boolean" &&
      typeof tier.required_approvals === "number",
  );

  return (
    <tr className="hover:bg-slate-50 dark:hover:bg-gray-800">
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-slate-800 dark:text-slate-200">
            {policy.name}
          </span>
          {policy.is_active && (
            <CheckCircleIcon sx={{ fontSize: 14, color: "#10b981" }} />
          )}
          <Chip
            label={controlled ? "SoD enforced" : "Legacy controls"}
            size="small"
            color={controlled ? "success" : "warning"}
            variant="outlined"
            sx={{ fontSize: 10, height: 18 }}
          />
        </div>
        <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">
          v{policy.version} · Created {fmtDate((policy as ApprovalPolicy & { created_at?: string }).created_at ?? "")}
        </p>
      </td>
      <td className="px-4 py-3 text-xs text-slate-600 dark:text-slate-300">
        {sourceLabel}
      </td>
      <td className="px-4 py-3 text-xs text-slate-600 dark:text-slate-300">
        {policy.tiers_json.length} tier{policy.tiers_json.length !== 1 ? "s" : ""}
        {policy.tiers_json.length > 0 && (
          <ul className="mt-0.5 space-y-0.5">
            {policy.tiers_json.map((t, i) => (
              <li key={i} className="text-[11px] text-slate-400 dark:text-slate-500">
                T{i + 1}: {t.approver_roles.join(", ")} ·{" "}
                {t.any_or_all === "any" ? "any" : "all"} ·{" "}
                {t.any_or_all === "any" && (t.required_approvals ?? 1) > 1
                  ? `${t.required_approvals} signatures · `
                  : ""}
                {t.max_amount != null ? `≤ ${fmtMoney(t.max_amount)}` : "no limit"}
              </li>
            ))}
          </ul>
        )}
      </td>
      <td className="px-4 py-3">
        <Chip
          label={policy.is_active ? "Active" : "Inactive"}
          size="small"
          color={policy.is_active ? "success" : "default"}
          variant={policy.is_active ? "filled" : "outlined"}
          sx={{ fontSize: 11, height: 22 }}
        />
      </td>
      <td className="px-4 py-3">
        <Chip
          label={policy.ai_assist ? "AI assist on" : "AI assist off"}
          size="small"
          color={policy.ai_assist ? "primary" : "default"}
          variant="outlined"
          sx={{ fontSize: 11, height: 22 }}
        />
      </td>
      <td className="px-4 py-3">
        {!policy.is_active && (
          <button
            type="button"
            disabled={activateMutation.isPending}
            onClick={() => activateMutation.mutate()}
            className="text-xs px-2.5 py-1 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 transition flex items-center gap-1"
          >
            {activateMutation.isPending ? (
              <CircularProgress size={10} sx={{ color: "#fff" }} />
            ) : null}
            Activate
          </button>
        )}
        {policy.is_active && (
          <Tooltip title="This is the active policy. Activate another policy to replace it.">
            <span className="text-xs text-slate-400 dark:text-slate-500 cursor-help">
              Active
            </span>
          </Tooltip>
        )}
      </td>
    </tr>
  );
}

function ActivateProposalDialog({
  proposal,
  reviewerName,
  open,
  onClose,
  onActivated,
}: {
  proposal: ControlPolicyProposal | null;
  reviewerName: string;
  open: boolean;
  onClose: () => void;
  onActivated: () => void;
}) {
  const [confirmation, setConfirmation] = useState("");
  const activate = useMutation({
    mutationFn: () =>
      api(`/api/money/approvals/control-implementation/proposals/${proposal?.id}/activate`, {
        method: "POST",
        body: { confirmation },
      }),
    onSuccess: () => {
      setConfirmation("");
      onActivated();
      onClose();
    },
  });

  if (!proposal) return null;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 600 }}>
        Activate this material-review policy?
      </DialogTitle>
      <DialogContent dividers>
        <div className="space-y-3 text-sm text-slate-700 dark:text-slate-200">
          <p>{proposal.rationale}</p>
          <div className="rounded-lg bg-slate-50 p-3 text-xs dark:bg-gray-900">
            <p><span className="font-semibold">Source:</span> {proposal.source_type.replaceAll("_", " ")}</p>
            <p><span className="font-semibold">Inclusive floor:</span> {fmtMoney(Number(proposal.minimum_amount))}</p>
            <p><span className="font-semibold">Named reviewer:</span> {reviewerName}</p>
            <p><span className="font-semibold">Effect:</span> smaller activity remains direct; matching new activity requires the named reviewer.</p>
          </div>
          <label className="block">
            <span className="text-xs font-medium">Type the exact confirmation</span>
            <code className="mt-1 block rounded bg-slate-100 px-2 py-1 text-xs dark:bg-gray-800">
              {proposal.activation_confirmation}
            </code>
            <input
              value={confirmation}
              onChange={(e) => setConfirmation(e.target.value)}
              className="mt-2 w-full rounded-md border border-slate-300 px-3 py-2 font-mono text-xs dark:border-gray-700 dark:bg-gray-900"
            />
          </label>
          {activate.isError && (
            <p className="text-xs text-red-600">{errorMessage(activate.error)}</p>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm text-slate-600">Cancel</button>
        <button
          type="button"
          disabled={activate.isPending || confirmation !== proposal.activation_confirmation}
          onClick={() => activate.mutate()}
          className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm text-white disabled:opacity-40"
        >
          {activate.isPending ? "Activating…" : "Activate policy"}
        </button>
      </DialogActions>
    </Dialog>
  );
}

// ──────────────────────────────────────────────────────────────
// Page
// ──────────────────────────────────────────────────────────────
function ApprovalPoliciesPage() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activationOpen, setActivationOpen] = useState(false);
  const qc = useQueryClient();
  const policiesQ = useQuery(approvalPoliciesQueryOptions());
  const policies: ApprovalPolicy[] = policiesQ.data ?? [];
  const activePolicy = policies.find((p) => p.is_active);
  const maturityQ = useQuery({
    queryKey: ["approvals", "control-maturity"],
    queryFn: () => api<ControlMaturityAssessment>("/api/money/approvals/control-maturity"),
  });
  const maturity = maturityQ.data;
  const pendingProposal = maturity?.implementation.pending_proposal ?? null;
  const acknowledge = useMutation({
    mutationFn: (noticeId: string) =>
      api(`/api/money/approvals/control-implementation/notices/${noticeId}/acknowledge`, {
        method: "POST",
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["approvals", "control-maturity"] }),
  });
  const dismissProposal = useMutation({
    mutationFn: (proposalId: string) =>
      api(`/api/money/approvals/control-implementation/proposals/${proposalId}/dismiss`, {
        method: "POST",
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["approvals", "control-maturity"] }),
  });

  const refreshControls = () => {
    void qc.invalidateQueries({ queryKey: ["approvals", "control-maturity"] });
    void qc.invalidateQueries({ queryKey: ["approvals", "policies"] });
  };

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden bg-slate-50 dark:bg-gray-950">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-4 dark:bg-gray-900 dark:border-gray-800">
        <div className="flex items-center gap-3">
          <PolicyIcon sx={{ fontSize: 22, color: "#3b82f6" }} />
          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
            Approval Policies
          </h1>
          {policiesQ.isFetching && <CircularProgress size={14} />}
          <div className="flex-1" />
          <button
            type="button"
            onClick={() => setDialogOpen(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-blue-600 text-white text-sm hover:bg-blue-700 transition"
          >
            <AddIcon sx={{ fontSize: 16 }} />
            New policy
          </button>
        </div>
        <p className="text-xs text-slate-500 mt-1 dark:text-slate-400">
          Start simple. Add targeted approval rules only when your team and
          actual use support them. Policies never turn on from a recommendation.
        </p>
        {activePolicy && (
          <p className="text-xs text-emerald-600 mt-1 dark:text-emerald-400 flex items-center gap-1">
            <CheckCircleIcon sx={{ fontSize: 13 }} />
            Active policy:{" "}
            <span className="font-semibold">{activePolicy.name}</span>
          </p>
        )}
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto p-6">
        {maturity && (
          <div className="mb-4 rounded-xl border border-indigo-200 bg-gradient-to-r from-indigo-50 to-white p-4 shadow-sm dark:border-indigo-900 dark:from-indigo-950/40 dark:to-gray-900">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-indigo-600 dark:text-indigo-400">
                  CFO progressive-controls review
                </p>
                <h2 className="mt-1 text-base font-semibold text-slate-900 dark:text-slate-100">
                  Recommended now: {maturity.recommended_stage.label}
                </h2>
                <p className="mt-1 max-w-3xl text-xs text-slate-600 dark:text-slate-300">
                  {maturity.recommended_stage.meaning}
                </p>
              </div>
              <a
                href={maturity.help_path}
                className="rounded-md border border-indigo-200 bg-white px-3 py-1.5 text-xs font-medium text-indigo-700 hover:bg-indigo-50 dark:border-indigo-800 dark:bg-gray-900 dark:text-indigo-300"
              >
                Read the guide
              </a>
            </div>
            <div className="mt-3 flex flex-wrap gap-2 text-[11px] text-slate-600 dark:text-slate-300">
              <span className="rounded-full bg-white px-2.5 py-1 dark:bg-gray-900">{maturity.evidence.active_users} active user{maturity.evidence.active_users === 1 ? "" : "s"}</span>
              <span className="rounded-full bg-white px-2.5 py-1 dark:bg-gray-900">{maturity.evidence.journals_90d} journals / 90d</span>
              <span className="rounded-full bg-white px-2.5 py-1 dark:bg-gray-900">{maturity.evidence.manual_journals_90d} manual</span>
              <span className="rounded-full bg-white px-2.5 py-1 dark:bg-gray-900">{maturity.evidence.approval_requests_90d} approval requests</span>
              <span className="rounded-full bg-white px-2.5 py-1 dark:bg-gray-900">Current setup: {maturity.current_stage.label}</span>
              <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300">Advisory only</span>
            </div>
            {maturity.recommendations[0] && (
              <p className="mt-3 text-xs text-slate-700 dark:text-slate-200">
                <span className="font-semibold">CFO suggestion:</span>{" "}
                {maturity.recommendations[0].action}. {maturity.recommendations[0].why}
              </p>
            )}
            <p className="mt-2 text-[11px] text-slate-500 dark:text-slate-400">
              <span className="font-semibold">Progression:</span>{" "}
              {maturity.progression.summary} Next scheduled review: {maturity.next_periodic_review_at.slice(0, 10)}.
            </p>
            <p className="mt-2 text-[11px] text-slate-400">
              Ask the CFO Advisor “Review whether our accounting controls fit how we work today.”
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-indigo-100 pt-3 dark:border-indigo-900">
              <span className="text-xs text-slate-600 dark:text-slate-300">
                <span className="font-semibold">Implementation:</span>{" "}
                {maturity.implementation.next_action}
              </span>
              <div className="flex-1" />
              {maturity.implementation.open_review_notice && (
                <button
                  type="button"
                  disabled={acknowledge.isPending}
                  onClick={() => acknowledge.mutate(maturity.implementation.open_review_notice!.id)}
                  className="rounded-md border border-indigo-200 bg-white px-2.5 py-1 text-xs text-indigo-700 hover:bg-indigo-50 disabled:opacity-50 dark:border-indigo-800 dark:bg-gray-900 dark:text-indigo-300"
                >
                  Mark review complete
                </button>
              )}
              {maturity.implementation.readiness === "ready_to_propose" && (
                <a
                  href="/chat"
                  className="rounded-md bg-indigo-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-indigo-700"
                >
                  Build proposal with CFO
                </a>
              )}
            </div>
            {pendingProposal && (
              <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-900 dark:bg-amber-950/30">
                <p className="text-xs font-semibold text-amber-800 dark:text-amber-200">Frozen proposal awaiting your decision</p>
                <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">{pendingProposal.rationale}</p>
                <p className="mt-1 text-[11px] text-amber-600 dark:text-amber-400">Expires {fmtDate(pendingProposal.expires_at)}. Nothing changes until activation.</p>
                <div className="mt-2 flex gap-2">
                  <button type="button" onClick={() => setActivationOpen(true)} className="rounded-md bg-indigo-600 px-2.5 py-1 text-xs text-white">Review and activate</button>
                  <button
                    type="button"
                    disabled={dismissProposal.isPending}
                    onClick={() => dismissProposal.mutate(pendingProposal.id)}
                    className="rounded-md border border-amber-300 px-2.5 py-1 text-xs text-amber-700 disabled:opacity-50 dark:text-amber-300"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
        <div className="mb-4 grid gap-3 md:grid-cols-3">
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 dark:border-emerald-900 dark:bg-emerald-950/30">
            <p className="text-xs font-semibold text-emerald-800 dark:text-emerald-300">Controls fit the team</p>
            <p className="mt-1 text-[11px] text-emerald-700 dark:text-emerald-400">One-person companies stay direct. Requester separation starts only with a real second reviewer.</p>
          </div>
          <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 dark:border-blue-900 dark:bg-blue-950/30">
            <p className="text-xs font-semibold text-blue-800 dark:text-blue-300">Matrix frozen per request</p>
            <p className="mt-1 text-[11px] text-blue-700 dark:text-blue-400">Policy version, eligible people, tiers, and quorum cannot drift mid-approval.</p>
          </div>
          <div className="rounded-lg border border-violet-200 bg-violet-50 px-4 py-3 dark:border-violet-900 dark:bg-violet-950/30">
            <p className="text-xs font-semibold text-violet-800 dark:text-violet-300">AI evidence preserved</p>
            <p className="mt-1 text-[11px] text-violet-700 dark:text-violet-400">Inputs, prompts, model, output, and SHA-256 hashes remain reproducible for audit.</p>
          </div>
        </div>
        {policiesQ.isError && (
          <div className="text-sm text-red-600 bg-red-50 rounded-lg px-4 py-3 border border-red-200 mb-4 dark:bg-red-950 dark:text-red-300 dark:border-red-800">
            Failed to load policies. {(policiesQ.error as Error)?.message}
          </div>
        )}

        {policies.length === 0 && !policiesQ.isFetching ? (
          <div className="bg-white border border-slate-200 rounded-lg shadow-sm px-8 py-12 text-center dark:bg-gray-900 dark:border-gray-800">
            <PolicyIcon sx={{ fontSize: 40, color: "#cbd5e1", mb: 1 }} />
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Simple mode is active.
            </p>
            <p className="text-xs text-slate-400 dark:text-slate-500 mt-1 mb-4">
              No routine approval policy is a valid setup for an owner or one
              bookkeeper. Audit evidence, confirmations, reconciliations,
              reversals, and close controls remain active.
            </p>
            <div className="flex flex-wrap justify-center gap-2">
              <a
                href="/help/docs/approvals/progressive-controls.md"
                className="inline-flex items-center rounded-md border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 dark:border-gray-700 dark:text-slate-300 dark:hover:bg-gray-800"
              >
                Understand the progression
              </a>
              <button
                type="button"
                onClick={() => setDialogOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700"
              >
                <AddIcon sx={{ fontSize: 16 }} />
                Add an optional policy
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-slate-200 overflow-hidden shadow-sm dark:bg-gray-900 dark:border-gray-800">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 dark:bg-gray-950 dark:border-gray-800">
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide dark:text-slate-400">
                    Name
                  </th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide dark:text-slate-400">
                    Applies to
                  </th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide dark:text-slate-400">
                    Tiers
                  </th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide dark:text-slate-400">
                    Status
                  </th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide dark:text-slate-400">
                    AI
                  </th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide dark:text-slate-400">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-gray-800">
                {policies.map((policy) => (
                  <PolicyRow key={policy.id} policy={policy} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <ActivateProposalDialog
        proposal={pendingProposal}
        reviewerName={
          maturity?.implementation.available_reviewers.find(
            (reviewer) => reviewer.id === pendingProposal?.reviewer_user_id,
          )?.name ?? "Named reviewer"
        }
        open={activationOpen}
        onClose={() => setActivationOpen(false)}
        onActivated={refreshControls}
      />

      <CreatePolicyDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
      />
    </div>
  );
}
